import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiGet, apiPost } from '../utils/api';

interface TelegramNotification {
  id: string;
  playerId: string;
  chatId: string;
  messageId: string | null;
  threadId: string | null;
  direction: string;
  type: string;
  text: string;
  metadata: string | null;
  status: string | null;
  errorMessage: string | null;
  readAt: string | null;
  createdAt: string;
  updatedAt: string;
  player: {
    id: string;
    name: string;
    telegramChatId: string | null;
    telegramUsername: string | null;
  };
  tournament?: {
    id: string;
    name: string;
  } | null;
  match?: {
    id: string;
  } | null;
  availability?: {
    id: string;
    date: string;
    startTime: string;
    endTime: string;
    court: {
      id: string;
      name: string;
      club: {
        id: string;
        name: string;
      };
    };
  } | null;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface MatchNotificationJob {
  id: string;
  type: string;
  matchId: string | null;
  contextType?: string | null;
  contextId?: string | null;
  targetTelegramId?: string | null;
  targetPlayerId?: string | null;
  payloadJson: string | null;
  sendAt: string;
  status: string;
  debounceKey: string | null;
  sentAt: string | null;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
  match?: {
    id: string;
    matchCode: string | null;
    level?: string | null;
    scheduledDate?: string | null;
    scheduledTime?: string | null;
    stageMatchNumber?: number | null;
    round?: { name: string } | null;
    player1?: { name?: string | null; websiteName?: string | null } | null;
    player2?: { name?: string | null; websiteName?: string | null } | null;
    player3?: { name?: string | null; websiteName?: string | null } | null;
    player4?: { name?: string | null; websiteName?: string | null } | null;
    previousMatches?: Array<{
      id: string;
      nextMatchSlot?: string | null;
      level?: string | null;
      stageMatchNumber?: number | null;
      matchNumber?: number | null;
      round?: { name: string } | null;
      winner?: { name?: string | null; websiteName?: string | null } | null;
      winner2?: { name?: string | null; websiteName?: string | null } | null;
    }>;
    tournament?: {
      id: string;
      name: string;
    } | null;
    court?: {
      name?: string | null;
      club?: { name?: string | null } | null;
    } | null;
  } | null;
  _count?: {
    deliveries: number;
  };
  deliveries?: Array<{
    id: string;
    telegramId?: string;
    status: string;
    errorMessage?: string | null;
    player?: {
      id: string;
      name?: string | null;
      websiteName?: string | null;
    } | null;
  }>;
  pendingRecipients?: Array<{
    telegramId?: string;
    playerId?: string | null;
    locale?: string;
    player?: {
      id: string;
      name?: string | null;
      websiteName?: string | null;
    } | null;
  }>;
}

interface MatchNotificationDelivery {
  id: string;
  jobId: string;
  matchId: string | null;
  contextType?: string | null;
  contextId?: string | null;
  telegramId: string;
  userId: string | null;
  playerId: string | null;
  type: string;
  status: string;
  errorMessage: string | null;
  sentAt: string;
  createdAt: string;
  match?: {
    id: string;
    matchCode: string | null;
    level?: string | null;
    scheduledDate?: string | null;
    scheduledTime?: string | null;
    stageMatchNumber?: number | null;
    round?: { name: string } | null;
    player1?: { name?: string | null; websiteName?: string | null } | null;
    player2?: { name?: string | null; websiteName?: string | null } | null;
    player3?: { name?: string | null; websiteName?: string | null } | null;
    player4?: { name?: string | null; websiteName?: string | null } | null;
    previousMatches?: Array<{
      id: string;
      nextMatchSlot?: string | null;
      level?: string | null;
      stageMatchNumber?: number | null;
      matchNumber?: number | null;
      round?: { name: string } | null;
      winner?: { name?: string | null; websiteName?: string | null } | null;
      winner2?: { name?: string | null; websiteName?: string | null } | null;
    }>;
    tournament?: {
      id: string;
      name: string;
    } | null;
    court?: {
      name?: string | null;
      club?: { name?: string | null } | null;
    } | null;
  } | null;
  job?: {
    id: string;
    type: string;
    status: string;
  } | null;
}

interface MatchNotificationSnapshotPayload {
  tournamentName?: string | null;
  level?: string | null;
  stage?: string | null;
  date?: string | null;
  time?: string | null;
  club?: string | null;
  court?: string | null;
  sideA?: string | null;
  sideB?: string | null;
}

interface PlayerFilterOption {
  id: string;
  name: string;
}

interface TournamentFilterOption {
  id: string;
  name: string;
  status: string;
}

interface PauseNewModeResponse {
  enabled: boolean;
  resumedCount?: number;
}

interface MatchNotificationSettingsState {
  pauseNewJobsEnabled: boolean;
  schedulerEnabled: boolean;
  schedulerIntervalMs: number;
  dbTimeoutBackoffMs: number;
  updateDebounceSeconds: number;
  globalSendDelayMs: number;
}

interface MatchNotificationQueueState {
  total: number;
  byStatus: Record<string, number>;
  oldestPendingJob?: {
    id: string;
    type: string;
    sendAt: string;
    createdAt: string;
  } | null;
  oldestPendingAgeSeconds?: number | null;
}

interface MatchNotificationSchedulerState {
  isRunning: boolean;
  schedulerEnabled: boolean;
  nextDelayMs: number | null;
  nextRunAt: string | null;
  lastTickStartedAt: string | null;
  lastTickFinishedAt: string | null;
  lastSuccessAt: string | null;
  lastErrorAt: string | null;
  lastErrorMessage: string | null;
  lastDurationMs: number | null;
}

interface MatchNotificationPlanModeState {
  key: string;
  tournamentId: string;
  tournamentName: string | null;
  enabled: boolean;
  startedAt: string | null;
  updatedAt: string | null;
  matchesCount: number;
  reservationContextsCount: number;
}

interface MatchNotificationSettingsResponse {
  settings: MatchNotificationSettingsState;
  queue: MatchNotificationQueueState;
  scheduler: MatchNotificationSchedulerState;
  planModes: MatchNotificationPlanModeState[];
}

type JobsSortField = 'createdAt' | 'sendAt' | 'sentAt';
type SortDirection = 'asc' | 'desc';
type NotificationsTab = 'messages' | 'jobs' | 'deliveries' | 'settings';

const RATE_LIMIT_STORAGE_KEY = 'telegramNotifications.rateLimitUntilMs';
const ACTIVE_TAB_STORAGE_KEY = 'telegramNotifications.activeTab';
const PAGINATION_LIMIT_OPTIONS = [10, 25, 50, 100] as const;
const JOB_TYPE_OPTIONS = [
  'match_scheduled',
  'match_updated',
  'match_cancelled',
  'match_reminder_24h',
  'match_reminder_2h',
  'slot_response_confirmation',
  'poll_vote_confirmation',
  'poll_deadline_expired',
  'pairing_task_created_alert',
  'pairing_task_assigned_alert',
  'pairing_task_tournament_group_notification',
  'system_incident_notification'
] as const;

const getSavedRateLimitUntil = (): number => {
  if (typeof window === 'undefined') return 0;
  const raw = window.sessionStorage.getItem(RATE_LIMIT_STORAGE_KEY);
  const parsed = Number(raw || 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const saveRateLimitUntil = (untilMs: number): void => {
  if (typeof window === 'undefined') return;
  if (untilMs > 0) {
    window.sessionStorage.setItem(RATE_LIMIT_STORAGE_KEY, String(untilMs));
    return;
  }
  window.sessionStorage.removeItem(RATE_LIMIT_STORAGE_KEY);
};

const getSavedActiveTab = (): NotificationsTab => {
  if (typeof window === 'undefined') return 'messages';
  const raw = String(window.localStorage.getItem(ACTIVE_TAB_STORAGE_KEY) || '').trim();
  if (raw === 'messages' || raw === 'jobs' || raw === 'deliveries' || raw === 'settings') return raw;
  return 'messages';
};

const saveActiveTab = (tab: NotificationsTab): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, tab);
};

const extractRetryAfterSeconds = (error: unknown): number => {
  const directRetry = Number((error as any)?.retryAfterSeconds || 0);
  if (Number.isFinite(directRetry) && directRetry > 0) {
    return directRetry;
  }

  const status = Number((error as any)?.status || 0);
  if (status === 429) return 60;

  const message = String((error as any)?.message || '');
  const retryMatch = message.match(/retry\s+after\s+(\d+)\s*seconds?/i);
  if (retryMatch) {
    const parsed = Number(retryMatch[1]);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  if (/429/.test(message)) return 60;
  return 0;
};

const TelegramNotifications: React.FC = () => {
  const [activeTab, setActiveTab] = useState<NotificationsTab>(() => getSavedActiveTab());
  const [rateLimitedUntil, setRateLimitedUntil] = useState<number>(() => getSavedRateLimitUntil());
  const rateLimitedUntilRef = useRef<number>(getSavedRateLimitUntil());
  const [manualRefreshing, setManualRefreshing] = useState(false);

  const [notifications, setNotifications] = useState<TelegramNotification[]>([]);
  const [messagesPagination, setMessagesPagination] = useState<Pagination>({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0
  });
  const [messagesLoading, setMessagesLoading] = useState(true);
  const [messageFilters, setMessageFilters] = useState({
    direction: 'OUTGOING' as 'INCOMING' | 'OUTGOING' | '',
    type: 'NOTIFICATION' as string,
    status: '' as string,
    playerId: '' as string,
    startDate: '' as string,
    endDate: '' as string
  });

  const [jobs, setJobs] = useState<MatchNotificationJob[]>([]);
  const [jobsPagination, setJobsPagination] = useState<Pagination>({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0
  });
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobActionLoading, setJobActionLoading] = useState<Record<string, boolean>>({});
  const [pauseNewJobsEnabled, setPauseNewJobsEnabled] = useState(false);
  const [pauseModeLoading, setPauseModeLoading] = useState(false);
  const [playerFilterOptions, setPlayerFilterOptions] = useState<PlayerFilterOption[]>([]);
  const [tournamentFilterOptions, setTournamentFilterOptions] = useState<TournamentFilterOption[]>([]);
  const [jobFilters, setJobFilters] = useState({
    status: '' as string,
    type: '' as string,
    tournamentId: '' as string,
    player: '' as string,
    startDate: '' as string,
    endDate: '' as string
  });
  const [jobsSort, setJobsSort] = useState<{ field: JobsSortField; direction: SortDirection }>({
    field: 'createdAt',
    direction: 'desc'
  });

  const [deliveries, setDeliveries] = useState<MatchNotificationDelivery[]>([]);
  const [deliveriesPagination, setDeliveriesPagination] = useState<Pagination>({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0
  });
  const [deliveriesLoading, setDeliveriesLoading] = useState(false);
  const [deliveryFilters, setDeliveryFilters] = useState({
    status: '' as string,
    type: '' as string,
    matchId: '' as string,
    player: '' as string,
    startDate: '' as string,
    endDate: '' as string
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsForm, setSettingsForm] = useState<MatchNotificationSettingsState | null>(null);
  const [queueState, setQueueState] = useState<MatchNotificationQueueState | null>(null);
  const [schedulerState, setSchedulerState] = useState<MatchNotificationSchedulerState | null>(null);
  const [planModes, setPlanModes] = useState<MatchNotificationPlanModeState[]>([]);
  const [settingsLastUpdatedAt, setSettingsLastUpdatedAt] = useState<string | null>(null);

  const getRateLimitRemainingSeconds = useCallback((): number => {
    const now = Date.now();
    const remainingMs = Math.max(0, rateLimitedUntilRef.current - now);
    return Math.ceil(remainingMs / 1000);
  }, []);

  const isRateLimited = useCallback((): boolean => {
    const now = Date.now();
    return rateLimitedUntilRef.current > now;
  }, []);

  const applyRateLimit = useCallback((retryAfterSeconds: number) => {
    const now = Date.now();
    const seconds = Math.max(5, retryAfterSeconds || 60);
    const until = now + (seconds * 1000);
    rateLimitedUntilRef.current = until;
    setRateLimitedUntil(until);
    saveRateLimitUntil(until);
  }, []);

  const clearRateLimitIfExpired = useCallback(() => {
    const now = Date.now();
    if (rateLimitedUntilRef.current > 0 && rateLimitedUntilRef.current <= now) {
      rateLimitedUntilRef.current = 0;
      setRateLimitedUntil(0);
      saveRateLimitUntil(0);
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    clearRateLimitIfExpired();
    if (isRateLimited()) {
      setMessagesLoading(false);
      return;
    }
    setMessagesLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', messagesPagination.page.toString());
      params.append('limit', messagesPagination.limit.toString());
      params.append('type', 'NOTIFICATION');
      
      if (messageFilters.direction) {
        params.append('direction', messageFilters.direction);
      }
      if (messageFilters.status) {
        params.append('status', messageFilters.status);
      }
      if (messageFilters.playerId) {
        params.append('playerId', messageFilters.playerId);
      }
      if (messageFilters.startDate) {
        params.append('startDate', messageFilters.startDate);
      }
      if (messageFilters.endDate) {
        params.append('endDate', messageFilters.endDate);
      }

      const data = await apiGet<{ messages: TelegramNotification[]; pagination: Pagination }>(
        `/telegram-messages?${params.toString()}`
      );
      
      setNotifications(data.messages);
      setMessagesPagination(data.pagination);
    } catch (error) {
      const retryAfterSeconds = extractRetryAfterSeconds(error);
      if (retryAfterSeconds > 0) {
        applyRateLimit(retryAfterSeconds);
      } else {
        console.error('Ошибка загрузки уведомлений:', error);
      }
    } finally {
      setMessagesLoading(false);
    }
  }, [messagesPagination.page, messagesPagination.limit, messageFilters, clearRateLimitIfExpired, isRateLimited, applyRateLimit]);

  const fetchPlayerFilterOptions = useCallback(async () => {
    clearRateLimitIfExpired();
    if (isRateLimited()) return;
    try {
      const data = await apiGet<{ players?: Array<{ id: string; name: string }>; items?: Array<{ id: string; name: string }> } | Array<{ id: string; name: string }>>(
        '/players?isActive=true&limit=1000'
      );
      const source = Array.isArray(data)
        ? data
        : Array.isArray(data.players)
          ? data.players
          : Array.isArray(data.items)
            ? data.items
            : [];
      const normalized = source
        .map((item) => ({
          id: String(item.id || ''),
          name: String(item.name || '').trim()
        }))
        .filter((item) => item.id && item.name)
        .sort((a, b) => a.name.localeCompare(b.name, 'ru'));
      setPlayerFilterOptions(normalized);
    } catch (error) {
      const retryAfterSeconds = extractRetryAfterSeconds(error);
      if (retryAfterSeconds > 0) {
        applyRateLimit(retryAfterSeconds);
      } else {
        console.error('Ошибка загрузки игроков для фильтра:', error);
      }
    }
  }, [clearRateLimitIfExpired, isRateLimited, applyRateLimit]);

  const fetchTournamentFilterOptions = useCallback(async () => {
    clearRateLimitIfExpired();
    if (isRateLimited()) return;
    try {
      const data = await apiGet<{ tournaments?: any[]; items?: any[] } | any[]>('/tournaments?limit=1000');
      const source = Array.isArray(data)
        ? data
        : Array.isArray((data as any)?.tournaments)
          ? (data as any).tournaments
          : Array.isArray((data as any)?.items)
            ? (data as any).items
            : [];
      const normalized = source
        .map((item: any) => ({
          id: String(item?.id || ''),
          name: String(item?.name || '').trim(),
          status: String(item?.status || '').trim().toUpperCase()
        }))
        .filter((item: TournamentFilterOption) => item.id && item.name)
        .sort((a: TournamentFilterOption, b: TournamentFilterOption) => {
          const aActive = a.status === 'ACTIVE' ? 0 : 1;
          const bActive = b.status === 'ACTIVE' ? 0 : 1;
          if (aActive !== bActive) return aActive - bActive;
          return a.name.localeCompare(b.name, 'ru');
        });
      setTournamentFilterOptions(normalized);
    } catch (error) {
      const retryAfterSeconds = extractRetryAfterSeconds(error);
      if (retryAfterSeconds > 0) {
        applyRateLimit(retryAfterSeconds);
      } else {
        console.error('Ошибка загрузки турниров для фильтра:', error);
      }
    }
  }, [clearRateLimitIfExpired, isRateLimited, applyRateLimit]);

  const fetchJobs = useCallback(async () => {
    clearRateLimitIfExpired();
    if (isRateLimited()) {
      setJobsLoading(false);
      return;
    }
    setJobsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', jobsPagination.page.toString());
      params.append('limit', jobsPagination.limit.toString());
      if (jobFilters.status) params.append('status', jobFilters.status);
      if (jobFilters.type) params.append('type', jobFilters.type);
      if (jobFilters.tournamentId) params.append('tournamentId', jobFilters.tournamentId.trim());
      if (jobFilters.player) params.append('player', jobFilters.player.trim());
      if (jobFilters.startDate) params.append('startDate', jobFilters.startDate);
      if (jobFilters.endDate) params.append('endDate', jobFilters.endDate);
      params.append('sortBy', jobsSort.field);
      params.append('sortOrder', jobsSort.direction);

      const data = await apiGet<{ jobs: MatchNotificationJob[]; pagination: Pagination }>(
        `/telegram-messages/match-notification-jobs?${params.toString()}`
      );
      setJobs(data.jobs);
      setJobsPagination(data.pagination);
    } catch (error) {
      const retryAfterSeconds = extractRetryAfterSeconds(error);
      if (retryAfterSeconds > 0) {
        applyRateLimit(retryAfterSeconds);
      } else {
        console.error('Ошибка загрузки jobs уведомлений:', error);
      }
    } finally {
      setJobsLoading(false);
    }
  }, [jobsPagination.page, jobsPagination.limit, jobFilters, jobsSort, clearRateLimitIfExpired, isRateLimited, applyRateLimit]);

  const fetchPauseNewJobsMode = useCallback(async () => {
    clearRateLimitIfExpired();
    if (isRateLimited()) return;
    try {
      const data = await apiGet<PauseNewModeResponse>('/telegram-messages/match-notification-jobs/pause-new-mode');
      setPauseNewJobsEnabled(Boolean(data?.enabled));
    } catch (error) {
      const retryAfterSeconds = extractRetryAfterSeconds(error);
      if (retryAfterSeconds > 0) {
        applyRateLimit(retryAfterSeconds);
      } else {
        console.error('Ошибка загрузки режима "Новые на паузу":', error);
      }
    }
  }, [clearRateLimitIfExpired, isRateLimited, applyRateLimit]);

  const fetchDeliveries = useCallback(async () => {
    clearRateLimitIfExpired();
    if (isRateLimited()) {
      setDeliveriesLoading(false);
      return;
    }
    setDeliveriesLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', deliveriesPagination.page.toString());
      params.append('limit', deliveriesPagination.limit.toString());
      if (deliveryFilters.status) params.append('status', deliveryFilters.status);
      if (deliveryFilters.type) params.append('type', deliveryFilters.type);
      if (deliveryFilters.matchId) params.append('matchId', deliveryFilters.matchId.trim());
      if (deliveryFilters.player) params.append('player', deliveryFilters.player.trim());
      if (deliveryFilters.startDate) params.append('startDate', deliveryFilters.startDate);
      if (deliveryFilters.endDate) params.append('endDate', deliveryFilters.endDate);

      const data = await apiGet<{ deliveries: MatchNotificationDelivery[]; pagination: Pagination }>(
        `/telegram-messages/match-notification-deliveries?${params.toString()}`
      );
      setDeliveries(data.deliveries);
      setDeliveriesPagination(data.pagination);
    } catch (error) {
      const retryAfterSeconds = extractRetryAfterSeconds(error);
      if (retryAfterSeconds > 0) {
        applyRateLimit(retryAfterSeconds);
      } else {
        console.error('Ошибка загрузки deliveries уведомлений:', error);
      }
    } finally {
      setDeliveriesLoading(false);
    }
  }, [deliveriesPagination.page, deliveriesPagination.limit, deliveryFilters, clearRateLimitIfExpired, isRateLimited, applyRateLimit]);

  const fetchNotificationSettings = useCallback(async () => {
    clearRateLimitIfExpired();
    if (isRateLimited()) return;
    setSettingsLoading(true);
    try {
      const data = await apiGet<MatchNotificationSettingsResponse>('/telegram-messages/match-notification-settings');
      setSettingsForm(data.settings);
      setQueueState(data.queue);
      setSchedulerState(data.scheduler);
      setPlanModes(Array.isArray(data.planModes) ? data.planModes : []);
      setSettingsLastUpdatedAt(new Date().toISOString());
    } catch (error) {
      const retryAfterSeconds = extractRetryAfterSeconds(error);
      if (retryAfterSeconds > 0) {
        applyRateLimit(retryAfterSeconds);
      } else {
        console.error('Ошибка загрузки настроек уведомлений:', error);
      }
    } finally {
      setSettingsLoading(false);
    }
  }, [clearRateLimitIfExpired, isRateLimited, applyRateLimit]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      clearRateLimitIfExpired();
    }, 1000);
    return () => window.clearInterval(timer);
  }, [clearRateLimitIfExpired]);

  useEffect(() => {
    saveActiveTab(activeTab);
  }, [activeTab]);

  useEffect(() => {
    fetchPlayerFilterOptions();
  }, [fetchPlayerFilterOptions]);

  useEffect(() => {
    fetchTournamentFilterOptions();
  }, [fetchTournamentFilterOptions]);

  useEffect(() => {
    fetchPauseNewJobsMode();
  }, [fetchPauseNewJobsMode]);

  useEffect(() => {
    if (activeTab === 'messages') fetchNotifications();
  }, [activeTab, fetchNotifications]);

  useEffect(() => {
    if (activeTab === 'jobs') fetchJobs();
  }, [activeTab, fetchJobs]);

  useEffect(() => {
    if (activeTab === 'deliveries') fetchDeliveries();
  }, [activeTab, fetchDeliveries]);

  useEffect(() => {
    if (activeTab === 'settings') fetchNotificationSettings();
  }, [activeTab, fetchNotificationSettings]);

  useEffect(() => {
    if (activeTab !== 'settings') return;
    const timer = window.setInterval(() => {
      void fetchNotificationSettings();
    }, 10_000);
    return () => window.clearInterval(timer);
  }, [activeTab, fetchNotificationSettings]);

  const handleManualRefresh = useCallback(async () => {
    clearRateLimitIfExpired();
    if (isRateLimited()) return;
    setManualRefreshing(true);
    try {
      await fetchPlayerFilterOptions();
      await fetchTournamentFilterOptions();
      await fetchPauseNewJobsMode();
      if (activeTab === 'messages') {
        await fetchNotifications();
      } else if (activeTab === 'jobs') {
        await fetchJobs();
      } else if (activeTab === 'settings') {
        await fetchNotificationSettings();
      } else {
        await fetchDeliveries();
      }
    } finally {
      setManualRefreshing(false);
    }
  }, [
    activeTab,
    clearRateLimitIfExpired,
    isRateLimited,
    fetchPlayerFilterOptions,
    fetchTournamentFilterOptions,
    fetchPauseNewJobsMode,
    fetchNotifications,
    fetchJobs,
    fetchNotificationSettings,
    fetchDeliveries
  ]);

  const handleTogglePauseNewJobs = useCallback(async (enabled: boolean) => {
    setPauseModeLoading(true);
    try {
      const data = await apiPost<PauseNewModeResponse>('/telegram-messages/match-notification-jobs/pause-new-mode', { enabled });
      setPauseNewJobsEnabled(Boolean(data?.enabled));
      if (activeTab === 'jobs') {
        await fetchJobs();
      }
    } catch (error) {
      alert('Не удалось изменить режим "Новые на паузу"');
      console.error('Ошибка переключения режима "Новые на паузу":', error);
    } finally {
      setPauseModeLoading(false);
    }
  }, [activeTab, fetchJobs]);

  const handleSaveNotificationSettings = useCallback(async () => {
    if (!settingsForm) return;
    setSettingsSaving(true);
    try {
      const data = await apiPost<{ settings?: MatchNotificationSettingsState; resumedCount?: number }>(
        '/telegram-messages/match-notification-settings',
        {
          pauseNewJobsEnabled: settingsForm.pauseNewJobsEnabled,
          schedulerIntervalMs: settingsForm.schedulerIntervalMs,
          dbTimeoutBackoffMs: settingsForm.dbTimeoutBackoffMs,
          updateDebounceSeconds: settingsForm.updateDebounceSeconds,
          globalSendDelayMs: settingsForm.globalSendDelayMs
        }
      );
      if (data?.settings) {
        setSettingsForm(data.settings);
        setPauseNewJobsEnabled(Boolean(data.settings.pauseNewJobsEnabled));
      }
      await fetchNotificationSettings();
      if ((data?.resumedCount || 0) > 0) {
        window.alert(`Возобновлено paused jobs: ${data.resumedCount}`);
      }
    } catch (error) {
      console.error('Ошибка сохранения настроек уведомлений:', error);
      window.alert('Не удалось сохранить настройки уведомлений');
    } finally {
      setSettingsSaving(false);
    }
  }, [settingsForm, fetchNotificationSettings]);

  const handlePageChange = (newPage: number) => {
    if (activeTab === 'messages') {
      setMessagesPagination(prev => ({ ...prev, page: newPage }));
      return;
    }
    if (activeTab === 'jobs') {
      setJobsPagination(prev => ({ ...prev, page: newPage }));
      return;
    }
    setDeliveriesPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleJobsSort = (field: JobsSortField) => {
    setJobsPagination((prev) => ({ ...prev, page: 1 }));
    setJobsSort((prev) => {
      if (prev.field === field) {
        return { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { field, direction: 'desc' };
    });
  };

  const getSortArrow = (field: JobsSortField): string => {
    if (jobsSort.field !== field) return '↕';
    return jobsSort.direction === 'asc' ? '↑' : '↓';
  };

  const handlePageLimitChange = (newLimit: number) => {
    if (!Number.isFinite(newLimit) || newLimit <= 0) return;
    if (activeTab === 'messages') {
      setMessagesPagination((prev) => ({ ...prev, page: 1, limit: newLimit }));
      return;
    }
    if (activeTab === 'jobs') {
      setJobsPagination((prev) => ({ ...prev, page: 1, limit: newLimit }));
      return;
    }
    setDeliveriesPagination((prev) => ({ ...prev, page: 1, limit: newLimit }));
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateWithBullet = (dateString: string) => {
    return formatDate(dateString).replace(',', ' •');
  };

  const getJobSnapshotFromPayload = (job: MatchNotificationJob): MatchNotificationSnapshotPayload | null => {
    const payloadJson = String(job.payloadJson || '').trim();
    if (!payloadJson) return null;
    try {
      const parsed = JSON.parse(payloadJson);
      const snapshot = parsed?.newSnapshot;
      if (!snapshot || typeof snapshot !== 'object') return null;
      return snapshot as MatchNotificationSnapshotPayload;
    } catch {
      return null;
    }
  };

  const formatSnapshotScheduleLine = (snapshot: MatchNotificationSnapshotPayload | null): string => {
    if (!snapshot) return '';
    const date = String(snapshot.date || '').trim();
    const time = String(snapshot.time || '').trim();
    const club = String(snapshot.club || '').trim();
    const court = String(snapshot.court || '').trim();
    const hasAny = Boolean(date || time || club || court);
    if (!hasAny) return '';
    return `${date || '—'} • ${time || '—'} • ${club || '—'} • ${court || '—'}`;
  };

  const formatSnapshotTitle = (snapshot: MatchNotificationSnapshotPayload | null): string => {
    if (!snapshot) return '';
    const level = String(snapshot.level || '').trim();
    const stage = String(snapshot.stage || '').trim();
    if (level && stage) return `${level} • ${stage}`;
    return level || stage || '';
  };

  const formatSnapshotSides = (snapshot: MatchNotificationSnapshotPayload | null): string => {
    if (!snapshot) return '';
    const sideA = String(snapshot.sideA || '').trim();
    const sideB = String(snapshot.sideB || '').trim();
    if (!sideA && !sideB) return '';
    return `${sideA || 'X'} vs ${sideB || 'X'}`;
  };

  const formatNullableDateTime = (value?: string | null): string => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return formatDate(value);
  };

  const formatSeconds = (value?: number | null): string => {
    if (!Number.isFinite(value as number) || (value as number) < 0) return '—';
    const total = Number(value || 0);
    const min = Math.floor(total / 60);
    const sec = total % 60;
    if (min <= 0) return `${sec}с`;
    return `${min}м ${sec}с`;
  };

  const getJobSentDelayMs = (job: MatchNotificationJob): number | null => {
    if (!job.sentAt) return null;
    const sendAtMs = new Date(job.sendAt).getTime();
    const sentAtMs = new Date(job.sentAt).getTime();
    if (!Number.isFinite(sendAtMs) || !Number.isFinite(sentAtMs)) return null;
    return sentAtMs - sendAtMs;
  };

  const getStatusBadge = (status: string | null) => {
    if (!status) return null;
    
    switch (status) {
      case 'SENT':
        return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Отправлено</span>;
      case 'DELIVERED':
        return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Доставлено</span>;
      case 'READ':
        return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">Прочитано</span>;
      case 'FAILED':
        return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Ошибка</span>;
      default:
        return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  const getJobStatusBadge = (status: string) => {
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'pending') return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">pending</span>;
    if (normalized === 'paused') return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">paused</span>;
    if (normalized === 'processing') return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">processing</span>;
    if (normalized === 'sent') return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">sent</span>;
    if (normalized === 'cancelled') return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">cancelled</span>;
    if (normalized === 'failed') return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">failed</span>;
    return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{status}</span>;
  };

  const getDeliveryStatusBadge = (status: string) => {
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'sent') return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">sent</span>;
    if (normalized === 'failed') return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">failed</span>;
    if (normalized === 'skipped') return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">skipped</span>;
    return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{status}</span>;
  };

  const getDeliveryStatusEmoji = (status?: string | null): string => {
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'sent') return '✅';
    if (normalized === 'failed') return '❌';
    if (normalized === 'skipped') return '⏭️';
    return '•';
  };

  const resolveDeliveryRecipientName = (
    delivery?: { player?: { name?: string | null; websiteName?: string | null } | null } | null
  ): string => {
    const websiteName = String(delivery?.player?.websiteName || '').trim();
    if (websiteName) return websiteName;
    const name = String(delivery?.player?.name || '').trim();
    if (name) return name;
    return 'Неизвестный получатель';
  };

  const resolvePendingRecipientName = (
    recipient?: { telegramId?: string; player?: { name?: string | null; websiteName?: string | null } | null } | null
  ): string => {
    const websiteName = String(recipient?.player?.websiteName || '').trim();
    if (websiteName) return websiteName;
    const name = String(recipient?.player?.name || '').trim();
    if (name) return name;
    const telegramId = String(recipient?.telegramId || '').trim();
    if (telegramId) return `telegram:${telegramId}`;
    return 'Ожидаемый получатель';
  };

  const handleRetryJobDelivery = async (deliveryId: string, recipientName: string) => {
    const confirmed = window.confirm(`Повторно отправить уведомление получателю "${recipientName}"?`);
    if (!confirmed) return;
    try {
      await apiPost(`/telegram-messages/match-notification-deliveries/${deliveryId}/retry`, {});
      await fetchJobs();
    } catch (error) {
      console.error('Ошибка повторной отправки delivery:', error);
      window.alert('Не удалось повторно отправить уведомление');
    }
  };

  const withJobActionLoading = async (key: string, fn: () => Promise<void>) => {
    setJobActionLoading((prev) => ({ ...prev, [key]: true }));
    try {
      await fn();
    } finally {
      setJobActionLoading((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleCloneJob = async (job: MatchNotificationJob) => {
    try {
      await withJobActionLoading(`${job.id}:clone`, async () => {
        await apiPost(`/telegram-messages/match-notification-jobs/${job.id}/clone`, {});
        await fetchJobs();
      });
    } catch (error: any) {
      console.error('Ошибка копирования job:', error);
      window.alert(error?.message || 'Не удалось создать копию job');
    }
  };

  const handleTogglePauseJob = async (job: MatchNotificationJob) => {
    try {
      await withJobActionLoading(`${job.id}:toggle`, async () => {
        await apiPost(`/telegram-messages/match-notification-jobs/${job.id}/toggle-pause`, {});
        await fetchJobs();
      });
    } catch (error: any) {
      console.error('Ошибка паузы/возобновления job:', error);
      window.alert(error?.message || 'Не удалось изменить статус job');
    }
  };

  const handleCancelJob = async (job: MatchNotificationJob) => {
    const confirmed = window.confirm(`Отменить job "${job.type}"?`);
    if (!confirmed) return;
    try {
      await withJobActionLoading(`${job.id}:cancel`, async () => {
        await apiPost(`/telegram-messages/match-notification-jobs/${job.id}/cancel`, {});
        await fetchJobs();
      });
    } catch (error: any) {
      console.error('Ошибка отмены job:', error);
      window.alert(error?.message || 'Не удалось отменить job');
    }
  };

  const resolvePlayerName = (player?: { name?: string | null; websiteName?: string | null } | null): string => {
    const websiteName = String(player?.websiteName || '').trim();
    if (websiteName) return websiteName;
    return String(player?.name || '').trim();
  };

  const resolveStageLabel = (roundName?: string | null): string => {
    const value = String(roundName || '').trim();
    if (!value) return '';
    if (value.includes('1/8')) return '1/8';
    if (value.includes('1/4')) return '1/4';
    if (value.includes('1/2')) return '1/2';
    if (/финал/i.test(value)) return 'Финал';
    return value;
  };

  const formatUnknownFromPrevious = (prevMatch: {
    level?: string | null;
    stageMatchNumber?: number | null;
    matchNumber?: number | null;
    round?: { name: string } | null;
    winner?: { name?: string | null; websiteName?: string | null } | null;
    winner2?: { name?: string | null; websiteName?: string | null } | null;
  } | undefined): string => {
    if (!prevMatch) return 'X';
    const winnerName = resolvePlayerName(prevMatch.winner);
    const winner2Name = resolvePlayerName(prevMatch.winner2);
    if (winnerName && winner2Name) return `${winnerName} / ${winner2Name}`;
    if (winnerName) return winnerName;
    const stageLabel = resolveStageLabel(prevMatch.round?.name);
    const number = prevMatch.stageMatchNumber || prevMatch.matchNumber || null;
    const level = String(prevMatch.level || '').trim();
    if (level && stageLabel && number) return `${level} • ${stageLabel} • ${number}`;
    if (stageLabel && number) return `${stageLabel} • ${number}`;
    return 'X';
  };

  const formatMatchSides = (
    match?: {
      level?: string | null;
      player1?: { name?: string | null; websiteName?: string | null } | null;
      player2?: { name?: string | null; websiteName?: string | null } | null;
      player3?: { name?: string | null; websiteName?: string | null } | null;
      player4?: { name?: string | null; websiteName?: string | null } | null;
      previousMatches?: Array<{
        nextMatchSlot?: string | null;
        level?: string | null;
        stageMatchNumber?: number | null;
        matchNumber?: number | null;
        round?: { name: string } | null;
        winner?: { name?: string | null; websiteName?: string | null } | null;
        winner2?: { name?: string | null; websiteName?: string | null } | null;
      }>;
    } | null
  ): string => {
    if (!match) return '';
    const isDoubles = Boolean(match.player3 || match.player4);

    if (isDoubles) {
      const sideAPlayers = [resolvePlayerName(match.player1), resolvePlayerName(match.player3)].filter(Boolean);
      const sideBPlayers = [resolvePlayerName(match.player2), resolvePlayerName(match.player4)].filter(Boolean);
      const sideA = sideAPlayers.length > 0 ? sideAPlayers.join(' / ') : 'X / X';
      const sideB = sideBPlayers.length > 0 ? sideBPlayers.join(' / ') : 'X / X';
      return `${sideA} vs ${sideB}`;
    }

    const sideAName = resolvePlayerName(match.player1);
    const sideBName = resolvePlayerName(match.player2);

    const previousHome = match.previousMatches?.find(
      (item) => String(item.nextMatchSlot || '').toLowerCase() === 'home'
    );
    const previousAway = match.previousMatches?.find(
      (item) => String(item.nextMatchSlot || '').toLowerCase() === 'away'
    );

    const sideA = sideAName || formatUnknownFromPrevious(previousHome);
    const sideB = sideBName || formatUnknownFromPrevious(previousAway);
    if (!sideA && !sideB) return '';
    return `${sideA || 'X'} vs ${sideB || 'X'}`;
  };

  const formatMatchTitle = (
    match?: {
      matchCode: string | null;
      level?: string | null;
      stageMatchNumber?: number | null;
      round?: { name: string } | null;
    } | null
  ): string => {
    if (!match) return '';
    const rawCode = String(match.matchCode || '').trim();
    const level = String(match.level || '').trim();
    const isPlayoffCode = /^playoff-\d+$/i.test(rawCode) || /^плей-офф\s*-\s*\d+$/i.test(rawCode);
    if (isPlayoffCode && match.round?.name) {
      const stageNo = match.stageMatchNumber || null;
      if (level && stageNo) return `${level} • ${match.round.name} • ${stageNo}`;
      if (level) return `${level} • ${match.round.name}`;
      return stageNo ? `${match.round.name} • ${stageNo}` : match.round.name;
    }
    if (!rawCode) return level || '—';
    const normalizedRaw = rawCode.toLowerCase();
    const normalizedLevel = level.toLowerCase();
    const rawAlreadyHasLevel = normalizedLevel
      ? normalizedRaw === normalizedLevel || normalizedRaw.startsWith(`${normalizedLevel} `) || normalizedRaw.startsWith(`${normalizedLevel} •`)
      : false;
    if (level && !rawAlreadyHasLevel) return `${level} • ${rawCode}`;
    return rawCode;
  };

  const formatMatchScheduleLine = (
    match?: {
      scheduledDate?: string | null;
      scheduledTime?: string | null;
      court?: {
        name?: string | null;
        club?: { name?: string | null } | null;
      } | null;
    } | null
  ): string => {
    if (!match) return '';
    const dateValue = String(match.scheduledDate || '').trim();
    const timeValue = String(match.scheduledTime || '').trim();
    const clubValue = String(match.court?.club?.name || '').trim();
    const courtValue = String(match.court?.name || '').trim();

    const dateFormatted = dateValue
      ? new Date(dateValue).toLocaleDateString('ru-RU', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        })
      : '—';

    const hasAny = Boolean(dateValue || timeValue || clubValue || courtValue);
    if (!hasAny) return '';

    return `${dateFormatted} • ${timeValue || '—'} • ${clubValue || '—'} • ${courtValue || '—'}`;
  };

  return (
    <div className="p-4">
      <div className="mb-4">
        <h1 className="text-xl font-bold text-gray-900">Уведомления Telegram</h1>
        <p className="mt-0.5 text-xs text-gray-500">
          Просмотр и управление уведомлениями, отправленными игрокам через Telegram
        </p>
      </div>

      {rateLimitedUntil > Date.now() && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          API временно ограничил запросы (429). Повторите через ~{getRateLimitRemainingSeconds()} сек.
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2 mb-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('messages')}
              className={`px-3 py-2 rounded-lg text-sm ${activeTab === 'messages' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              Telegram сообщения
            </button>
            <button
              onClick={() => setActiveTab('jobs')}
              className={`px-3 py-2 rounded-lg text-sm ${activeTab === 'jobs' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              Notification Jobs
            </button>
            <button
              onClick={() => setActiveTab('deliveries')}
              className={`px-3 py-2 rounded-lg text-sm ${activeTab === 'deliveries' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              Notification Deliveries
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`px-3 py-2 rounded-lg text-sm ${activeTab === 'settings' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              Настройки уведомлений
            </button>
          </div>
          <button
            type="button"
            onClick={handleManualRefresh}
            disabled={manualRefreshing || isRateLimited()}
            className="px-3 py-2 rounded-lg text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {manualRefreshing ? 'Обновляем…' : 'Обновить'}
          </button>
        </div>
      </div>

      {/* Фильтры */}
      {activeTab !== 'settings' && (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="text-base font-semibold text-gray-900">Фильтры</h2>
          {activeTab === 'jobs' && (
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 select-none">
              <span>Новые на паузу</span>
              <input
                type="checkbox"
                checked={pauseNewJobsEnabled}
                disabled={pauseModeLoading}
                onChange={(e) => handleTogglePauseNewJobs(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-60"
              />
            </label>
          )}
        </div>
        {activeTab === 'messages' && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Направление</label>
            <select
              value={messageFilters.direction}
              onChange={(e) => setMessageFilters({ ...messageFilters, direction: e.target.value as any })}
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Все</option>
              <option value="OUTGOING">Исходящие</option>
              <option value="INCOMING">Входящие</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Статус</label>
            <select
              value={messageFilters.status}
              onChange={(e) => setMessageFilters({ ...messageFilters, status: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Все</option>
              <option value="SENT">Отправлено</option>
              <option value="DELIVERED">Доставлено</option>
              <option value="READ">Прочитано</option>
              <option value="FAILED">Ошибка</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Дата с</label>
            <input
              type="date"
              value={messageFilters.startDate}
              onChange={(e) => setMessageFilters({ ...messageFilters, startDate: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Дата по</label>
            <input
              type="date"
              value={messageFilters.endDate}
              onChange={(e) => setMessageFilters({ ...messageFilters, endDate: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        )}

        {activeTab === 'jobs' && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Статус</label>
              <select
                value={jobFilters.status}
                onChange={(e) => setJobFilters({ ...jobFilters, status: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Все</option>
                <option value="pending">pending</option>
                <option value="paused">paused</option>
                <option value="processing">processing</option>
                <option value="sent">sent</option>
                <option value="cancelled">cancelled</option>
                <option value="failed">failed</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Тип</label>
              <select
                value={jobFilters.type}
                onChange={(e) => setJobFilters({ ...jobFilters, type: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Все типы</option>
                {JOB_TYPE_OPTIONS.map((jobType) => (
                  <option key={jobType} value={jobType}>
                    {jobType}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Турнир</label>
              <select
                value={jobFilters.tournamentId}
                onChange={(e) => setJobFilters({ ...jobFilters, tournamentId: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Все турниры</option>
                {tournamentFilterOptions.map((tournament) => (
                  <option
                    key={tournament.id}
                    value={tournament.id}
                    style={{ color: tournament.status === 'ACTIVE' ? '#111827' : '#9ca3af' }}
                  >
                    {tournament.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Игрок</label>
              <select
                value={jobFilters.player}
                onChange={(e) => setJobFilters({ ...jobFilters, player: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Все игроки</option>
                {playerFilterOptions.map((player) => (
                  <option key={player.id} value={player.id}>
                    {player.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Дата с</label>
              <input
                type="date"
                value={jobFilters.startDate}
                onChange={(e) => setJobFilters({ ...jobFilters, startDate: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Дата по</label>
              <input
                type="date"
                value={jobFilters.endDate}
                onChange={(e) => setJobFilters({ ...jobFilters, endDate: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        )}

        {activeTab === 'deliveries' && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Статус</label>
              <select
                value={deliveryFilters.status}
                onChange={(e) => setDeliveryFilters({ ...deliveryFilters, status: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Все</option>
                <option value="sent">sent</option>
                <option value="failed">failed</option>
                <option value="skipped">skipped</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Тип</label>
              <input
                type="text"
                value={deliveryFilters.type}
                onChange={(e) => setDeliveryFilters({ ...deliveryFilters, type: e.target.value })}
                placeholder="match_scheduled"
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Match ID</label>
              <input
                type="text"
                value={deliveryFilters.matchId}
                onChange={(e) => setDeliveryFilters({ ...deliveryFilters, matchId: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Игрок</label>
              <select
                value={deliveryFilters.player}
                onChange={(e) => setDeliveryFilters({ ...deliveryFilters, player: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Все игроки</option>
                {playerFilterOptions.map((player) => (
                  <option key={player.id} value={player.id}>
                    {player.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Дата с</label>
              <input
                type="date"
                value={deliveryFilters.startDate}
                onChange={(e) => setDeliveryFilters({ ...deliveryFilters, startDate: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Дата по</label>
              <input
                type="date"
                value={deliveryFilters.endDate}
                onChange={(e) => setDeliveryFilters({ ...deliveryFilters, endDate: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        )}
      </div>
      )}

      {/* Список уведомлений */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {activeTab === 'messages' && (messagesLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500">Загрузка...</div>
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500">Уведомления не найдены</div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Дата</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Игрок</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Текст</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Статус</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Контекст</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {notifications.map((notification) => (
                    <tr key={notification.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-xs text-gray-900">
                        {formatDate(notification.createdAt)}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-900">
                        <div>
                          <div className="font-medium">{notification.player.name}</div>
                          {notification.player.telegramUsername && (
                            <div className="text-gray-500">@{notification.player.telegramUsername}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-900 max-w-md">
                        <div className="truncate" title={notification.text}>
                          {notification.text}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {getStatusBadge(notification.status)}
                        {notification.errorMessage && (
                          <div className="mt-1 text-red-600 text-xs" title={notification.errorMessage}>
                            ⚠️ {notification.errorMessage.substring(0, 50)}...
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-500">
                        {notification.tournament && (
                          <div>🏆 Турнир: {notification.tournament.name}</div>
                        )}
                        {notification.match && (
                          <div>🎾 Матч: {notification.match.id}</div>
                        )}
                        {notification.availability && (
                          <div>
                            🕐 Слот: {new Date(notification.availability.date).toLocaleDateString('ru-RU')} {notification.availability.startTime}-{notification.availability.endTime}
                            <br />
                            🏢 {notification.availability.court.club.name}, корт {notification.availability.court.name}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Пагинация */}
            {messagesPagination.total > 0 && (
              <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                <div className="text-xs text-gray-700">
                  Показано {((messagesPagination.page - 1) * messagesPagination.limit) + 1} - {Math.min(messagesPagination.page * messagesPagination.limit, messagesPagination.total)} из {messagesPagination.total}
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-600">
                    На странице:
                    <select
                      value={messagesPagination.limit}
                      onChange={(e) => handlePageLimitChange(Number(e.target.value))}
                      className="ml-2 border border-gray-300 rounded-lg px-2 py-1 text-xs"
                    >
                      {PAGINATION_LIMIT_OPTIONS.map((limitOption) => (
                        <option key={limitOption} value={limitOption}>{limitOption}</option>
                      ))}
                    </select>
                  </label>
                  <button
                    onClick={() => handlePageChange(messagesPagination.page - 1)}
                    disabled={messagesPagination.page === 1}
                    className="px-3 py-1 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Назад
                  </button>
                  <span className="px-3 py-1 text-xs text-gray-700">
                    Страница {messagesPagination.page} из {messagesPagination.pages}
                  </span>
                  <button
                    onClick={() => handlePageChange(messagesPagination.page + 1)}
                    disabled={messagesPagination.page === messagesPagination.pages}
                    className="px-3 py-1 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Вперед
                  </button>
                </div>
              </div>
            )}
          </>
        ))}

        {activeTab === 'jobs' && (jobsLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500">Загрузка...</div>
          </div>
        ) : jobs.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500">Jobs не найдены</div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">
                      <button
                        type="button"
                        onClick={() => handleJobsSort('createdAt')}
                        className="inline-flex items-center gap-1 hover:text-gray-900"
                        title="Сортировать по дате создания"
                      >
                        Создано <span>{getSortArrow('createdAt')}</span>
                      </button>
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Тип</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Статус</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">
                      <button
                        type="button"
                        onClick={() => handleJobsSort('sendAt')}
                        className="inline-flex items-center gap-1 hover:text-gray-900"
                        title="Сортировать по дате отправки"
                      >
                        Отправить в <span>{getSortArrow('sendAt')}</span>
                      </button>
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">
                      <button
                        type="button"
                        onClick={() => handleJobsSort('sentAt')}
                        className="inline-flex items-center gap-1 hover:text-gray-900"
                        title="Сортировать по фактической дате отправки"
                      >
                        Отправлено <span>{getSortArrow('sentAt')}</span>
                      </button>
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Матч</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Deliveries</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Действия</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {jobs.map((job) => {
                    const delayMs = getJobSentDelayMs(job);
                    const isSentDelayed = delayMs !== null && delayMs > 2 * 60 * 1000;
                    const isSentVeryDelayed = delayMs !== null && delayMs > 10 * 60 * 1000;
                    const sentDelayTitle = isSentDelayed ? `Задержка отправки: ~${Math.round((delayMs as number) / 60000)} мин` : '';
                    const snapshot = getJobSnapshotFromPayload(job);
                    const snapshotScheduleLine = formatSnapshotScheduleLine(snapshot);
                    const snapshotTournament = String(snapshot?.tournamentName || '').trim();
                    const snapshotTitle = formatSnapshotTitle(snapshot);
                    const snapshotSides = formatSnapshotSides(snapshot);

                    return (
                    <tr key={job.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-xs">{formatDateWithBullet(job.createdAt)}</td>
                      <td className="px-4 py-2 text-xs">{job.type}</td>
                      <td className="px-4 py-2 text-xs">{getJobStatusBadge(job.status)}</td>
                      <td className="px-4 py-2 text-xs">{formatDateWithBullet(job.sendAt)}</td>
                      <td className="px-4 py-2 text-xs" title={sentDelayTitle}>
                        {job.sentAt ? (
                          isSentDelayed ? (
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-900 border ${
                                isSentVeryDelayed ? 'border-red-500' : 'border-yellow-300'
                              }`}
                            >
                              {formatDateWithBullet(job.sentAt)}
                              <svg
                                className={`ml-1 h-3.5 w-3.5 ${isSentVeryDelayed ? 'text-red-600' : 'text-amber-600'}`}
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                aria-hidden="true"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm.75-11.25a.75.75 0 0 0-1.5 0v4.5a.75.75 0 0 0 1.5 0v-4.5Zm0 7a.75.75 0 1 0-1.5 0 .75.75 0 0 0 1.5 0Z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </span>
                          ) : (
                            formatDateWithBullet(job.sentAt)
                          )
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-700">
                        {snapshotScheduleLine && (
                          <div className="text-gray-500">{snapshotScheduleLine}</div>
                        )}
                        <div className="text-gray-500">{snapshotTournament}</div>
                        <div>{snapshotTitle || '—'}</div>
                        {snapshotSides && (
                          <div className="text-gray-500">{snapshotSides}</div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-700">
                        {Array.isArray(job.deliveries) && job.deliveries.length > 0 ? (
                          <div>
                            {job.deliveries.map((delivery) => (
                              <div key={delivery.id} className="whitespace-nowrap">
                                {delivery.status === 'failed' ? (
                                  <button
                                    type="button"
                                    onClick={() => handleRetryJobDelivery(delivery.id, resolveDeliveryRecipientName(delivery))}
                                    className="text-left text-blue-600 hover:text-blue-800 underline decoration-dotted"
                                    title="Повторно отправить"
                                  >
                                    {getDeliveryStatusEmoji(delivery.status)} {resolveDeliveryRecipientName(delivery)}
                                  </button>
                                ) : (
                                  <span>
                                    {getDeliveryStatusEmoji(delivery.status)} {resolveDeliveryRecipientName(delivery)}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : String(job.status || '').toLowerCase() === 'pending' && Array.isArray(job.pendingRecipients) && job.pendingRecipients.length > 0 ? (
                          <div>
                            {job.pendingRecipients.map((recipient, idx) => (
                              <div key={`${recipient.playerId || recipient.telegramId || 'pending'}_${idx}`} className="whitespace-nowrap">
                                ⏳ {resolvePendingRecipientName(recipient)}
                              </div>
                            ))}
                          </div>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleCloneJob(job)}
                            disabled={Boolean(jobActionLoading[`${job.id}:clone`])}
                            className="text-gray-700 hover:text-gray-900 disabled:opacity-50"
                            title="Создать копированием"
                          >
                            📄
                          </button>
                          <button
                            type="button"
                            onClick={() => handleTogglePauseJob(job)}
                            disabled={
                              Boolean(jobActionLoading[`${job.id}:toggle`]) ||
                              !['pending', 'paused'].includes(String(job.status || '').toLowerCase())
                            }
                            className="text-gray-700 hover:text-gray-900 disabled:opacity-50"
                            title={String(job.status || '').toLowerCase() === 'paused' ? 'Возобновить действие' : 'Приостановить действие'}
                          >
                            {String(job.status || '').toLowerCase() === 'paused' ? '▶️' : '⏸️'}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleCancelJob(job)}
                            disabled={
                              Boolean(jobActionLoading[`${job.id}:cancel`]) ||
                              !['pending', 'paused'].includes(String(job.status || '').toLowerCase())
                            }
                            className="text-red-600 hover:text-red-700 disabled:opacity-50"
                            title="Отменить действие"
                          >
                            ❌
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>
            {jobsPagination.total > 0 && (
              <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                <div className="text-xs text-gray-700">
                  Показано {((jobsPagination.page - 1) * jobsPagination.limit) + 1} - {Math.min(jobsPagination.page * jobsPagination.limit, jobsPagination.total)} из {jobsPagination.total}
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-600">
                    На странице:
                    <select
                      value={jobsPagination.limit}
                      onChange={(e) => handlePageLimitChange(Number(e.target.value))}
                      className="ml-2 border border-gray-300 rounded-lg px-2 py-1 text-xs"
                    >
                      {PAGINATION_LIMIT_OPTIONS.map((limitOption) => (
                        <option key={limitOption} value={limitOption}>{limitOption}</option>
                      ))}
                    </select>
                  </label>
                  <button onClick={() => handlePageChange(jobsPagination.page - 1)} disabled={jobsPagination.page === 1} className="px-3 py-1 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">Назад</button>
                  <span className="px-3 py-1 text-xs text-gray-700">Страница {jobsPagination.page} из {jobsPagination.pages}</span>
                  <button onClick={() => handlePageChange(jobsPagination.page + 1)} disabled={jobsPagination.page === jobsPagination.pages} className="px-3 py-1 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">Вперед</button>
                </div>
              </div>
            )}
          </>
        ))}

        {activeTab === 'deliveries' && (deliveriesLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500">Загрузка...</div>
          </div>
        ) : deliveries.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500">Deliveries не найдены</div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Дата</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Тип</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Статус</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Telegram ID</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Job</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Матч</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Ошибка</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {deliveries.map((delivery) => (
                    <tr key={delivery.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-xs">{formatDate(delivery.createdAt)}</td>
                      <td className="px-4 py-2 text-xs">{delivery.type}</td>
                      <td className="px-4 py-2 text-xs">{getDeliveryStatusBadge(delivery.status)}</td>
                      <td className="px-4 py-2 text-xs">{delivery.telegramId}</td>
                      <td className="px-4 py-2 text-xs">{delivery.job?.id || delivery.jobId}</td>
                      <td className="px-4 py-2 text-xs text-gray-700">
                        {formatMatchScheduleLine(delivery.match) && (
                          <div className="text-gray-500">{formatMatchScheduleLine(delivery.match)}</div>
                        )}
                        <div className="text-gray-500">{delivery.match?.tournament?.name || ''}</div>
                        <div>{formatMatchTitle(delivery.match) || delivery.matchId}</div>
                        {formatMatchSides(delivery.match) && (
                          <div className="text-gray-500">{formatMatchSides(delivery.match)}</div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs text-red-600">{delivery.errorMessage || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {deliveriesPagination.total > 0 && (
              <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                <div className="text-xs text-gray-700">
                  Показано {((deliveriesPagination.page - 1) * deliveriesPagination.limit) + 1} - {Math.min(deliveriesPagination.page * deliveriesPagination.limit, deliveriesPagination.total)} из {deliveriesPagination.total}
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-600">
                    На странице:
                    <select
                      value={deliveriesPagination.limit}
                      onChange={(e) => handlePageLimitChange(Number(e.target.value))}
                      className="ml-2 border border-gray-300 rounded-lg px-2 py-1 text-xs"
                    >
                      {PAGINATION_LIMIT_OPTIONS.map((limitOption) => (
                        <option key={limitOption} value={limitOption}>{limitOption}</option>
                      ))}
                    </select>
                  </label>
                  <button onClick={() => handlePageChange(deliveriesPagination.page - 1)} disabled={deliveriesPagination.page === 1} className="px-3 py-1 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">Назад</button>
                  <span className="px-3 py-1 text-xs text-gray-700">Страница {deliveriesPagination.page} из {deliveriesPagination.pages}</span>
                  <button onClick={() => handlePageChange(deliveriesPagination.page + 1)} disabled={deliveriesPagination.page === deliveriesPagination.pages} className="px-3 py-1 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">Вперед</button>
                </div>
              </div>
            )}
          </>
        ))}

        {activeTab === 'settings' && (
          <div className="p-4 space-y-4">
            {settingsLoading ? (
              <div className="text-gray-500">Загрузка настроек...</div>
            ) : !settingsForm ? (
              <div className="text-gray-500">Настройки недоступны</div>
            ) : (
              <>
                <div className="rounded-lg border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-900">Параметры обработки</h3>
                    <button
                      type="button"
                      onClick={handleSaveNotificationSettings}
                      disabled={settingsSaving || isRateLimited()}
                      className="px-3 py-1.5 rounded-lg text-sm bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                      {settingsSaving ? 'Сохраняем…' : 'Сохранить'}
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    <label className="text-xs text-gray-700">
                      <span className="block mb-1">Новые jobs на паузу</span>
                      <input
                        type="checkbox"
                        checked={Boolean(settingsForm.pauseNewJobsEnabled)}
                        onChange={(e) => setSettingsForm({ ...settingsForm, pauseNewJobsEnabled: e.target.checked })}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </label>
                    <label className="text-xs text-gray-700">
                      <span className="block mb-1">Интервал тика (ms)</span>
                      <input
                        type="number"
                        min={1000}
                        step={1000}
                        value={settingsForm.schedulerIntervalMs}
                        onChange={(e) => setSettingsForm({ ...settingsForm, schedulerIntervalMs: Number(e.target.value || 0) })}
                        className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                      />
                    </label>
                    <label className="text-xs text-gray-700">
                      <span className="block mb-1">Backoff БД timeout (ms)</span>
                      <input
                        type="number"
                        min={5000}
                        step={1000}
                        value={settingsForm.dbTimeoutBackoffMs}
                        onChange={(e) => setSettingsForm({ ...settingsForm, dbTimeoutBackoffMs: Number(e.target.value || 0) })}
                        className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                      />
                    </label>
                    <label className="text-xs text-gray-700">
                      <span className="block mb-1">Debounce match_updated (сек)</span>
                      <input
                        type="number"
                        min={0}
                        step={10}
                        value={settingsForm.updateDebounceSeconds}
                        onChange={(e) => setSettingsForm({ ...settingsForm, updateDebounceSeconds: Number(e.target.value || 0) })}
                        className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                      />
                    </label>
                    <label className="text-xs text-gray-700">
                      <span className="block mb-1">Пауза между отправками (ms)</span>
                      <input
                        type="number"
                        min={0}
                        step={100}
                        value={settingsForm.globalSendDelayMs}
                        onChange={(e) => setSettingsForm({ ...settingsForm, globalSendDelayMs: Number(e.target.value || 0) })}
                        className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                      />
                    </label>
                    <div className="text-xs text-gray-700">
                      <span className="block mb-1">Планировщик (env)</span>
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${settingsForm.schedulerEnabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'}`}>
                        {settingsForm.schedulerEnabled ? 'включен' : 'выключен'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="rounded-lg border border-gray-200 p-3">
                    <div className="text-xs text-gray-500">Всего jobs (вся история)</div>
                    <div className="text-lg font-semibold text-gray-900">{queueState?.total ?? 0}</div>
                  </div>
                  <div className="rounded-lg border border-gray-200 p-3">
                    <div className="text-xs text-gray-500">Старейший pending</div>
                    <div className="text-sm font-medium text-gray-900">{formatSeconds(queueState?.oldestPendingAgeSeconds)}</div>
                  </div>
                  <div className="rounded-lg border border-gray-200 p-3">
                    <div className="text-xs text-gray-500">Последний успешный tick</div>
                    <div className="text-sm font-medium text-gray-900">{formatNullableDateTime(schedulerState?.lastSuccessAt)}</div>
                  </div>
                </div>

                <div className="rounded-lg border border-gray-200 p-3">
                  <div className="text-xs text-gray-500">Активная очередь (pending + processing + paused)</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {(queueState?.byStatus?.pending || 0) + (queueState?.byStatus?.processing || 0) + (queueState?.byStatus?.paused || 0)}
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    Обновлено: {formatNullableDateTime(settingsLastUpdatedAt)}
                  </div>
                </div>

                <div className="rounded-lg border border-gray-200 p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Состояние планировщика</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-gray-700">
                    <div>Выполняется сейчас: <b>{schedulerState?.isRunning ? 'да' : 'нет'}</b></div>
                    <div>Следующий запуск: <b>{formatNullableDateTime(schedulerState?.nextRunAt)}</b></div>
                    <div>Длительность последнего тика: <b>{schedulerState?.lastDurationMs ?? '—'} ms</b></div>
                    <div>Последняя ошибка: <b>{schedulerState?.lastErrorMessage || '—'}</b></div>
                  </div>
                </div>

                <div className="rounded-lg border border-gray-200 p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Статусы очереди</h3>
                  <div className="mb-2 text-xs text-gray-500">Кумулятивно по всей истории jobs</div>
                  <div className="flex flex-wrap gap-2 text-xs">
                    {Object.entries(queueState?.byStatus || {}).map(([status, count]) => (
                      <span key={status} className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                        {status}: {count}
                      </span>
                    ))}
                    {Object.keys(queueState?.byStatus || {}).length === 0 && (
                      <span className="text-gray-500">нет данных</span>
                    )}
                  </div>
                </div>

                <div className="rounded-lg border border-gray-200 p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Плановый режим по турнирам</h3>
                  {planModes.length === 0 ? (
                    <div className="text-xs text-gray-500">Записей нет</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-2 py-2 text-left text-xs font-medium text-gray-700">Турнир</th>
                            <th className="px-2 py-2 text-left text-xs font-medium text-gray-700">Включен</th>
                            <th className="px-2 py-2 text-left text-xs font-medium text-gray-700">Матчей в буфере</th>
                            <th className="px-2 py-2 text-left text-xs font-medium text-gray-700">Резервов в буфере</th>
                            <th className="px-2 py-2 text-left text-xs font-medium text-gray-700">Обновлено</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {planModes.map((row) => (
                            <tr key={row.key}>
                              <td className="px-2 py-2 text-xs text-gray-900">{row.tournamentName || row.tournamentId}</td>
                              <td className="px-2 py-2 text-xs">{row.enabled ? 'да' : 'нет'}</td>
                              <td className="px-2 py-2 text-xs">{row.matchesCount}</td>
                              <td className="px-2 py-2 text-xs">{row.reservationContextsCount}</td>
                              <td className="px-2 py-2 text-xs">{formatNullableDateTime(row.updatedAt)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TelegramNotifications;

